import React, { useState, useCallback, useMemo } from "react";
import SalesDocDetails from "../../_components/Sales/SalesDocDetails";
import { FiFileText, FiTag } from "react-icons/fi";
import { InfoRow, SearchableSelect, SimpleSelect } from "../../_components/Sales/SalesShared";
import { getDNById, updateDebitNote, createDebitNote } from "../../_services/finance.service";
import { getInvoices } from "../../_services/sales.service";
import { getCustomers } from "../../_services/contact.service";
import { companyProfileService } from "../../_services/companyProfile.service";

// ─── Constants ───────────────────────────────────────────────────────────────
const REASONS = [
    "Undercharged / Pricing Correction",
    "Additional Charges",
    "Interest on Overdue Payment",
    "Service Fee Adjustment"
];

let customerCache = {};

async function getCachedCustomer(customerName) {
    if (customerCache[customerName]) return customerCache[customerName];
    const res = await getCustomers({ pageSize: 1000, page: 1, search: customerName, searchType: "name" });
    customerCache[customerName] = res?.data || [];
    return customerCache[customerName];
}

async function fetchCustomerNames() {
    const res = await getCustomers({ pageSize: 1000, page: 1 });
    const names = (res?.data || [])
        .filter(r => (r.status || r.Status || "").toLowerCase() === "active")
        .map(r => r.name)
        .filter(Boolean);
    return [...new Set(names)].sort();
}

async function fetchContactPersons(customerName) {
    if (!customerName) return [];
    const data = await getCachedCustomer(customerName);
    return data
        .filter(r => r.name === customerName && !!r.attention && (r.status || r.Status || "").toLowerCase() === "active")
        .map(r => r.attention)
        .filter(Boolean)
        .filter((v, i, a) => a.indexOf(v) === i);
}

async function fetchCustomerDetails(customerName) {
    if (!customerName) return {};
    const data = await getCachedCustomer(customerName);
    const match = data.find(r => r.name === customerName);
    if (!match) return {};
    return {
        paymentTerms: match.creditTerms || "",
        customerAddress: match.billingAddress || match.address || match.shippingAddress || "",
        shippingAddress: match.shippingAddress || "",
        phoneNumber: match.phoneNumber || match.phone_number || "",
    };
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function DebitNoteDetails() {
    const [invoices, setInvoices] = useState([]);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    const fetchInvoices = useCallback((customerName, contactPerson) => {
        if (!customerName || !contactPerson) {
            setInvoices([]);
            setSelectedInvoice(null);
            return;
        }

        getInvoices({ pageSize: 500, page: 1, search: customerName, searchType: "customer", status: "Approved" })
            .then((res) => {
                const all = res?.data || [];
                setInvoices(all.filter(inv => inv.contactPerson === contactPerson));
            })
            .catch(() => setInvoices([]));
    }, []);


    const handleInvoiceSelect = useCallback((invoiceId) => {
        const found = invoices.find(inv => inv.id === invoiceId);
        setSelectedInvoice(found || null);
    }, [invoices]);

    const fillFromInvoice = useMemo(() => {
        if (!selectedInvoice) return null;
        return {
            customerAddress: selectedInvoice.customerAddress || "",
            items: (selectedInvoice.items || []).map(item => ({
                ...item,
                selected: true,
            })),
            invoiceId: selectedInvoice.id,
        };
    }, [selectedInvoice]);

    const dnExtra = useMemo(() => ({
        left: ({ draft, onChange, errors, edit, invoices: availableInvoices }) =>
            edit ? (
                <>
                    <SimpleSelect
                        label="Invoice Ref. No."
                        name="invoiceId"
                        value={draft?.invoiceId || ""}
                        onChange={(e) => {
                            onChange(e);
                            handleInvoiceSelect(e.target.value);
                        }}
                        options={(availableInvoices || []).map(inv => inv.id)}
                        placeholder="Select Invoice (Approved)..."
                        disabled={!draft?.customer || !draft?.contactPerson || (availableInvoices || []).length === 0}
                        error={errors?.invoiceId}
                    />
                    <SearchableSelect
                        label="Reason for Debit"
                        name="reason"
                        value={draft?.reason || ""}
                        onChange={onChange}
                        options={REASONS}
                        placeholder="Type or select a reason..."
                        required
                        disabled={!draft?.invoiceId}
                        error={errors?.reason}
                        allowFreeText={true}
                    />
                </>
            ) : (
                <>
                    {(draft?.invoiceId || draft?.invoice_id || draft?.invoiceRef) && (
                        <InfoRow icon={FiFileText} label="Invoice Ref.">
                            {draft.invoiceId || draft.invoice_id || draft.invoiceRef}
                        </InfoRow>
                    )}
                    {draft?.reason && (
                        <InfoRow icon={FiTag} label="Debit Reason">{draft.reason}</InfoRow>
                    )}
                </>
            ),
        right: null,
    }), [handleInvoiceSelect]);

    const fetchCustDetails = useCallback((name) => fetchCustomerDetails(name), []);
    const fetchCompProfile = useCallback(() => companyProfileService.getProfile(), []);

    return (
        <SalesDocDetails
            docType="Debit Note"
            basePath="/finance/debit-notes"
            fetchById={getDNById}
            updateItem={updateDebitNote}
            createItem={createDebitNote}
            fetchCustomerNames={fetchCustomerNames}
            fetchContactPersons={fetchContactPersons}
            fetchCustomerDetails={fetchCustDetails}
            fetchCompanyProfile={fetchCompProfile}
            taxRate={0}
            statuses={["Draft", "Pending for Approval", "Approved", "Rejected"]}
            emptyDraft={{ invoiceId: "", reason: "" }}
            extraFields={dnExtra}
            extraFieldProps={{ invoices }}
            hideCurrency={true}
            hidePaymentTerms={true}
            addressLabel="Customer Address"
            addressField="customerAddress"
            fillDraft={fillFromInvoice}
            onDependentFieldChange={fetchInvoices}
            onBeforeSave={(d) => ({ ...d, paymentTerms: "" })}
            forceNegativeTotals={false}
            totalLabel="Debit Amount (RM)"
            lineItemProps={{
                selectable: true,
                noPrice: false,
            }}
        />
    );
}
